"use client";

import { ListSkeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquarePlusIcon } from "@/components/ui/message-square-plus";
import { supabase } from "@/lib/supabase";
import { ChevronRightIcon, RefreshCcwIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

// ─── Types ────────────────────────────────────────────────────────────────────

type FeedbackStatus = "new" | "investigating" | "completed";

type MyFeedbackItem = {
  id: string;
  // null for meeting-recording feedback and for general feedback submitted
  // with no chat/project-chat/meeting target.
  chat_id: string | null;
  feedback: string;
  consent_to_read_chat: boolean;
  created_at: string;
  status: FeedbackStatus;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Matches the StatusBadge rendering in admin-chats-panel.tsx so statuses look
// the same to users and superadmins.
function StatusBadge({ status }: { status: FeedbackStatus }) {
  if (status === "completed") {
    return (
      <Badge
        variant="secondary"
        className="shrink-0 bg-activated text-activated-foreground px-1.5 py-0 text-[10px]"
      >
        Completed
      </Badge>
    );
  }
  if (status === "investigating") {
    return (
      <Badge
        variant="outline"
        className="shrink-0 px-1.5 py-0 text-[10px] text-accent-foreground border-accent-foreground/30 bg-accent/40"
      >
        Investigating
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="shrink-0 px-1.5 py-0 text-[10px] text-muted-foreground"
    >
      New
    </Badge>
  );
}

// ─── Panel ────────────────────────────────────────────────────────────────────

export type MyFeedbackPanelProps = {
  onBack?: () => void;
  /** Open the chat a feedback item was submitted on. */
  onOpenChat?: (chatId: string) => void;
};

export function MyFeedbackPanel({ onBack, onOpenChat }: MyFeedbackPanelProps) {
  const [feedbacks, setFeedbacks] = useState<MyFeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false);
  const [newFeedbackNote, setNewFeedbackNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadFeedbacks = useCallback(async () => {
    setLoading(true);
    setError(null);

    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      setLoading(false);
      setError("You need to be signed in to view your feedback.");
      return;
    }

    // RLS already limits reads to the caller's own rows; the explicit
    // user_id filter is defence in depth.
    const { data, error: queryError } = await supabase
      .from("chat_feedbacks")
      .select("id, chat_id, feedback, consent_to_read_chat, created_at, status")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    setLoading(false);

    if (queryError) {
      setError(queryError.message);
      return;
    }

    setFeedbacks((data as MyFeedbackItem[]) ?? []);
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadFeedbacks();
  }, [loadFeedbacks]);

  // Navigation only — no chat_feedbacks writes. Confirm the chat still exists
  // before navigating: the FK cascade normally removes feedback with its chat,
  // but guard against races. The query is RLS-scoped, so it can only ever
  // resolve a chat the signed-in user is allowed to read.
  const handleOpenChat = useCallback(
    async (chatId: string) => {
      if (!onOpenChat) return;

      const { data, error: chatError } = await supabase
        .from("chats")
        .select("id")
        .eq("id", chatId)
        .maybeSingle();

      if (chatError || !data) {
        toast.error("This chat is no longer available.");
        return;
      }

      onOpenChat(chatId);
    },
    [onOpenChat]
  );

  // General feedback — no chat/project chat/meeting recording target. Lets any
  // user report an issue even when they have nothing specific to point at.
  const handleSubmitFeedback = useCallback(async () => {
    const note = newFeedbackNote.trim();
    if (!note || isSubmitting) return;

    setIsSubmitting(true);

    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      toast.error("You need to be signed in to submit feedback.");
      setIsSubmitting(false);
      return;
    }

    const { error: insertError } = await supabase.from("chat_feedbacks").insert({
      user_id: user.id,
      feedback: note,
    });

    setIsSubmitting(false);

    if (insertError) {
      toast.error("Failed to submit feedback.");
      return;
    }

    toast.success("Feedback submitted. Thanks!");
    setNewFeedbackNote("");
    setIsSubmitDialogOpen(false);
    void loadFeedbacks();
  }, [newFeedbackNote, isSubmitting, loadFeedbacks]);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <PageHeader
        title="My Feedback"
        description="Feedback you've submitted and where the team is up to with it."
        onBack={onBack}
        actions={
          <div className="flex shrink-0 items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => void loadFeedbacks()}
              disabled={loading}
              aria-label="Refresh feedback"
              className="size-9 shrink-0"
            >
              <RefreshCcwIcon className="size-4" />
            </Button>
            <Button
              type="button"
              onClick={() => setIsSubmitDialogOpen(true)}
              className="gap-1.5"
            >
              <MessageSquarePlusIcon className="size-4" size={16} />
              <span className="hidden sm:inline">Submit feedback</span>
            </Button>
          </div>
        }
      />

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5">
        <div className="mx-auto w-full max-w-4xl space-y-3">
          {loading ? (
            <ListSkeleton />
          ) : error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : feedbacks.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              You haven&apos;t submitted any feedback yet.
            </p>
          ) : (
            feedbacks.map((item) => (
              <FeedbackCard
                key={item.id}
                item={item}
                onOpenChat={onOpenChat ? handleOpenChat : undefined}
              />
            ))
          )}
        </div>
      </div>

      <Dialog open={isSubmitDialogOpen} onOpenChange={setIsSubmitDialogOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Submit feedback</DialogTitle>
            <DialogDescription>
              Share anything on your mind — a bug, an idea, or general
              feedback. No chat needed.
            </DialogDescription>
          </DialogHeader>

          <Textarea
            className="min-h-32"
            disabled={isSubmitting}
            onChange={(event) => setNewFeedbackNote(event.currentTarget.value)}
            placeholder="Tell us what's up."
            value={newFeedbackNote}
          />

          <DialogFooter>
            <Button
              disabled={isSubmitting}
              onClick={() => setIsSubmitDialogOpen(false)}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              disabled={isSubmitting || !newFeedbackNote.trim()}
              onClick={() => void handleSubmitFeedback()}
              type="button"
            >
              {isSubmitting ? "Sending..." : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Feedback card ────────────────────────────────────────────────────────────

function FeedbackCard({
  item,
  onOpenChat,
}: {
  item: MyFeedbackItem;
  onOpenChat?: (chatId: string) => Promise<void>;
}) {
  const date = useMemo(
    () =>
      new Intl.DateTimeFormat("en-NZ", {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: "Pacific/Auckland",
      }).format(new Date(item.created_at)),
    [item.created_at]
  );

  const content = (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">Submitted {date} NZT</p>
        <div className="flex shrink-0 items-center gap-1.5">
          {item.consent_to_read_chat ? (
            <Badge
              variant="secondary"
              className="shrink-0 bg-activated text-activated-foreground px-1.5 py-0 text-[10px]"
            >
              Consent
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="shrink-0 px-1.5 py-0 text-[10px] text-muted-foreground"
            >
              No consent
            </Badge>
          )}
          <StatusBadge status={item.status ?? "new"} />
        </div>
      </div>
      <p className="whitespace-pre-wrap text-sm text-foreground">
        {item.feedback}
      </p>
    </>
  );

  // Feedback on a meeting recording (or without a chat reference) has no chat
  // to open — render the plain, non-clickable card.
  if (!onOpenChat || !item.chat_id) {
    return (
      <div className="space-y-2 rounded-lg border border-border bg-card px-4 py-3">
        {content}
      </div>
    );
  }

  const chatId = item.chat_id;

  return (
    <button
      type="button"
      onClick={() => void onOpenChat(chatId)}
      aria-label="Open the chat this feedback was submitted on"
      className="group relative block w-full rounded-lg border border-border bg-card px-4 py-3 pr-9 text-left transition-colors hover:bg-accent/50"
    >
      <div className="space-y-2">{content}</div>
      <ChevronRightIcon className="absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground/60 transition-colors group-hover:text-foreground" />
    </button>
  );
}
